<template>
  <div class="py-4 px-3 lg:px-6 bg-white-005 rounded-2xl min-h-400">
    <div class="flex justify-between items-center mb-4">
      <div class="text-sm lg:text-xl font-bold">Governance</div>
      <div>
        <UiDropdown
          :options="ProposalOptions"
          v-model="state.proposal"
          style="width: 120px"
        ></UiDropdown>
      </div>
    </div>
    <div class="overflow-x-auto min-h-full">
      <div class="lg:w-full" style="min-width: 860px">
        <div
          class="flex text-white-070 h-10 pr-0 lg:pr-3.5 text-xs lg:text-base"
        >
          <div
            class="pl-2 lg:pl-4 flex items-center"
            :style="`width: ${isLargeScreen ? '22.22%' : '22.3%'}`"
          >
            Proposals
          </div>
          <div
            class="pl-4 flex items-center"
            :style="`width: ${isLargeScreen ? '12.67%' : '12.6%'}`"
          >
            Platform
          </div>
          <div
            class="pl-4 flex items-center"
            :style="`width: ${isLargeScreen ? '17.19%' : '19%'}`"
          >
            Proposer
          </div>
          <div class="pl-4 flex items-center" style="width: 18.05%">DAO</div>
          <div
            class="pl-4 flex items-center text-left"
            :style="`width: ${isLargeScreen ? '8.3%' : '.3%'}`"
          >
            Voters
          </div>
          <div
            class="pr-4 flex justify-end items-center text-right"
            :style="`width: ${isLargeScreen ? '8.3%' : '6.3%'}`"
          >
            Votes
          </div>
          <div class="flex items-center justify-end" style="width: 13.2%">
            Status
          </div>
        </div>
        <div
          class="overflow-y-scroll w-full text-xs lg:text-base"
          ref="container"
          style="max-height: 448px"
        >
          <div v-for="(item, i) in results" :key="i" class="h-16">
            <div
              class="
                hover:bg-white-010
                transition-all
                rounded-lg
                cursor-pointer
                flex
                items-center
                h-full
              "
            >
              <div
                class="pl-2 lg:pl-4 flex items-center leading-6"
                :style="`width: ${isLargeScreen ? '22.22%' : '22.3%'}`"
              >
                <tippy :content="item.title" :arrow="false">
                  <div class="hover:underline line-clamp-2 font-bold">
                    <div
                      @click="
                        () => $router.push(`/proposal/details/${item.id}`)
                      "
                    >
                      {{
                        isLargeScreen
                          ? item.title.length > 40
                            ? item.title.substring(0, 40) + '...'
                            : item.title
                          : item.title.length > 36
                          ? item.title.substring(0, 36) + '...'
                          : item.title
                      }}
                      <span
                        v-if="item.category"
                        class="
                          text-xs
                          uppercase
                          rounded-lg
                          px-2
                          py-1
                          bg-white-010
                          whitespace-nowrap
                        "
                      >
                        {{ item.category }}
                      </span>
                    </div>
                  </div>
                </tippy>
              </div>
              <div
                class="pl-4 flex items-center leading-6 text-xs"
                :style="`width: ${isLargeScreen ? '12.67%' : '12.6%'}`"
              >
                <div
                  class="bg-snapshot rounded-lg px-1 lg:px-2"
                  style="min-width: 68px"
                  :style="`background:${
                    item.platform.toLowerCase() === 'tally'
                      ? 'linear-gradient(315deg, #725CFF 0%, #01E5CC 100%)'
                      : item.platform.toLowerCase() === 'nouns'
                      ? '#000AFF'
                      : item.platform.toLowerCase() === 'compound'
                      ? '#02D394'
                      : item.platform.toLowerCase() === 'openzeppelin'
                      ? '#4E5FE4'
                      : item.platform.toLowerCase() === 'kyber'
                      ? '#31CB9EFF'
                      : item.platform.toLowerCase() === 'osnap'
                      ? '#FF4A4AFF'
                      : item.platform.toLowerCase() === 'homebase'
                      ? '#81FEB7FF'
                      : item.platform.toLowerCase() === 'xdao'
                      ? '#0B4FCCFF'
                      : item.platform.toLowerCase() === 'colony'
                      ? '#FFD224FF'
                      : item.platform.toLowerCase() === 'aragon_tao' ||
                        item.platform.toLowerCase() === 'aragon_app'
                      ? '#3065FAFF'
                      : item.platform.toLowerCase() === 'aragon_client'
                      ? '#32FFF5FF'
                      : item.platform.toLowerCase() === 'makerdao'
                      ? '#2EB9AAFF'
                      : item.platform.toLowerCase() === 'partydao'
                      ? '#00C5FFFF'
                      : item.platform.toLowerCase() === 'agora'
                      ? '#0052FFFF'
                      : item.platform.toLowerCase() === 'retropgf'
                      ? '#FF0621FF'
                      : item.platform.toLowerCase() === 'aave'
                      ? 'linear-gradient(45deg, #31B9CA 0%, #B4509E 100%)'
                      : ''
                  }`"
                >
                  <i
                    class="iconfont text-xs"
                    :class="platformIconOnlyStyle(item.platform)"
                  ></i>

                  <span class="text-xs cursor-pointer font-bold">
                    {{
                      item.platform === 'openzeppelin'
                        ? item.platform.toLowerCase()
                        : item.platform?.toLowerCase() === 'osnap'
                        ? 'oSnap'
                        : item.platform.toLowerCase().slice(0, 6) === 'aragon'
                        ? item.platform
                            .split('_')
                            .map(
                              (word) => word[0].toUpperCase() + word.slice(1),
                            )
                            .join(' ')
                        : item.platform?.charAt(0).toUpperCase() +
                          item.platform?.slice(1)
                    }}
                  </span>
                </div>
              </div>
              <div
                class="pl-4 flex items-center leading-6"
                :style="`width: ${isLargeScreen ? '17.19%' : '19%'}`"
              >
                <div>
                  <EnsAddress
                    class="text-secondary-7"
                    :address="item.author"
                    :size="4"
                    :isCore="item.isCore"
                  ></EnsAddress>

                  <div>
                    <span class="text-white-040 text-xs px-1 py-0.5">
                      {{
                        formatDate(item.created * 1000, 'MMM DD, YYYY, H:mm A')
                      }}</span
                    >
                  </div>
                </div>
              </div>
              <router-link
                :to="`/dao/${item.daoPath}`"
                tag="div"
                class="pl-4 flex items-center hover:underline"
                style="width: 18.05%"
              >
                <UiImg
                  :src="item.logo"
                  class="w-8 h-8 rounded-full overflow-hidden mr-2"
                ></UiImg>
                <div class="line-clamp-1">
                  {{ item.name }}
                </div>
              </router-link>

              <div
                class="pl-4 text-left"
                :style="`width: ${isLargeScreen ? '8.3%' : '6.3%'}`"
              >
                {{ formatVal(item.voters, { omit: true, bit: 0 }) }}
              </div>
              <div
                class="pr-4 justify-end text-right"
                :style="`width: ${isLargeScreen ? '8.3%' : '6.3%'}`"
              >
                {{ formatVal(item.votes, { omit: true, bit: 0 }) }}
              </div>
              <div
                class="flex items-center text-xs justify-end"
                :style="`width: ${isLargeScreen ? '13.2%' : '16.5%'}`"
              >
                <div class="flex flex-col items-end">
                  <div class="w-max">
                    <div
                      v-if="item.state.toLowerCase() == 'pending'"
                      class="
                        h-8
                        rounded-lg
                        px-3
                        font-bold
                        flex
                        items-center
                        justify-center
                        bg-success
                      "
                    >
                      Pending
                    </div>
                    <div
                      v-else-if="
                        activeType.indexOf(item.state.toLowerCase()) !== -1
                      "
                      class="
                        h-8
                        w-max
                        rounded-lg
                        px-3
                        font-bold
                        flex
                        items-center
                        justify-center
                        bg-error
                        capitalize
                      "
                    >
                      {{ item.state.toLowerCase() }}
                    </div>
                    <div
                      v-else
                      class="
                        h-8
                        rounded-lg
                        px-3
                        w-max
                        font-bold
                        flex
                        items-center
                        justify-center
                        bg-white-010
                        capitalize
                      "
                    >
                      {{ item.state ? item.state.toLowerCase() : 'N/A' }}
                    </div>
                  </div>
                  <div
                    class="text-white-070 text-xs"
                    v-if="
                      activeType.indexOf(item.state.toLowerCase()) !== -1 ||
                      item.state.toLowerCase() == 'pending'
                    "
                  >
                    <i class="iconfont icon-shizhong-47 text-xs"></i>
                    {{
                      activeType.indexOf(item.state.toLowerCase()) !== -1
                        ? 'Closes '
                        : 'Starts '
                    }}
                    {{
                      formatDateText(
                        (activeType.indexOf(item.state.toLowerCase()) !== -1
                          ? item.end
                          : item.start) * 1000,
                      )
                    }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="loading" class="flex justify-center">
            <Spin size="2em"></Spin>
          </div>
          <div
            class="flex justify-center items-center text-white-070 h-20"
            v-if="isEmpty"
          >
            No Data
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { inject, onMounted, reactive, watch, ref } from 'vue';
import UiDropdown from './UiDropdown.vue';
import UiImg from '@/components/UiImg.vue';
import { useReqPages } from '@/hooks';
import { queryHomeGovernance } from '@/api/dao';
import { homeChains, homeTypes } from './constant';
import { useInfiniteScroll } from '@vueuse/core';
import { withThrottling } from '@/utils/with';
import Spin from '@/components/Spin.vue';
import { formatDate, formatDateText, formatVal, jumpSnapshot } from '@/utils';
import EnsAddress from './component/EnsAddress.vue';
import { useMediaQuery } from '@vueuse/core';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { platformIconOnlyStyle } from '@/constants/platforms';

const store = useStore();
const $router = useRouter();
const isLargeScreen = useMediaQuery('(min-width: 1024px)');
const homeState = inject('homeState');
const activeType = ['active', 'succeeded', 'queued'];

const ProposalOptions = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Pending', value: 'pending' },
  { label: 'Closed', value: 'closed' },
];

const container = ref(null);

function toggleBackdrop(item) {
  store.commit('setProposalId', item.id);
  store.commit('toggleBackdrop', true);
  $router.push(`/dao/${item.daoPath}/Governance`);
}
const state = reactive({
  proposal: ProposalOptions[0],
});

const { loadRest, loadNext, results, loading, isEmpty } = useReqPages(
  async (page, cancel) => {
    const res = await queryHomeGovernance(
      page,
      homeTypes[homeState.selectedType].value,
      homeChains[homeState.selectedChain].value,
      state.proposal.value,
      cancel,
    );

    const result = {
      lists: res.records,
      rows: res.size,
      total: res.total,
      pages: res.pages,
    };
    return result;
  },
);

watch(
  () => [homeState.selectedType, homeState.selectedChain, state.proposal],
  () => {
    loadRest(true);
  },
);

useInfiniteScroll(
  container,
  withThrottling(async () => {
    await loadNext();
  }),
  { distance: 44 },
);

onMounted(() => {
  loadRest();
});
</script>

<style scoped>
.a-style--main-red.home-proposal {
  height: 20px;
  font-weight: normal;
}
</style>
